"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import InfoCardsView from "@/components/optilase/hub/info-cards-view";
import { regionFromPathname } from "@/lib/region";
import { surfaceFrom } from "@/components/optilase/surface";
import { richParagraphs } from "@/lib/richtext";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { toDocumentCards, type DocumentCardBlok } from "@/lib/document-card";
import { usePublicPathname } from "@/lib/public-pathname";

type InfoCardBlok = SbBlokData & {
  eyebrow?: string;
  title?: string;
  description?: unknown;
  link_label?: string;
  link?: StoryblokLink;
};

type InfoCardGridBlok = SbBlokData & {
  heading?: string;
  intro?: string;
  link_label?: string;
  link?: StoryblokLink;
  cards?: InfoCardBlok[];
  documents?: DocumentCardBlok[];
  document_columns?: string;
  footnote?: string;
  layout?: string;
  image?: { filename?: string; alt?: string; focus?: string };
  bg_color?: unknown;
  card_color?: unknown;
};

export default function InfoCardGrid({ blok }: { blok: InfoCardGridBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const link = resolveLink(blok.link, region);

  return (
    <div {...storyblokEditable(blok)}>
      <InfoCardsView
        surface={surfaceFrom(blok.bg_color)}
        cardSurface={surfaceFrom(blok.card_color)}
        heading={blok.heading ?? ""}
        intro={blok.intro}
        linkLabel={blok.link_label || undefined}
        linkHref={link.href}
        layout={
          blok.layout === "grid" || blok.layout === "grid_3" || blok.layout === "image"
            ? blok.layout
            : "beside"
        }
        image={blok.image?.filename || undefined}
        imageAlt={blok.image?.alt || ""}
        imageFocus={blok.image?.focus}
        footnote={blok.footnote}
        documents={toDocumentCards(blok.documents, region)}
        documentColumns={blok.document_columns}
        cards={(blok.cards ?? []).map((card) => ({
          eyebrow: card.eyebrow,
          title: card.title ?? "",
          body: richParagraphs(
            card.description,
            region,
            (surfaceFrom(blok.card_color) ?? surfaceFrom(blok.bg_color))?.onDark,
          ),
          linkLabel: card.link_label || undefined,
          href: resolveLink(card.link, region).href,
        }))}
      />
    </div>
  );
}
